// Usage.tsx — Cockpit Verbrauchs-Anzeige (TQ #10589, SPEC-Cockpit-Verbrauchsanzeige.md
// §4a/§6, Zielbild PROJ-Cortex-Billing).
//
// Zeigt dem Kunden (org-Kontext) oder dem Personal-Mode-User den Monatsverbrauch
// aufgeschlüsselt nach Nutzungsart (Aria-Chat / Aufgaben-Arbeit / Mensch-
// Ereignisse) — IT-SCE-Einheiten + EUR, KEINE Provider-/Modellnamen
// (Währungs-Grundsatz, project_arbeit-in-token-analyse).
//
// Quelle: GET /api/org/:slug/usage (org) bzw. GET /api/me/usage (Personal-Mode),
// siehe services/portal-api/internal/handlers/org_usage.go.

import { useCallback, useEffect, useState } from 'react'
import { useAuth } from '@it-sce/ui/contexts/KeycloakAuthContext'
import { api } from '../services/api'
import type { UsageResponse } from '../services/api'
import { isPersonalMode } from '../utils/cockpitMode'
import { formatInt } from '../utils/formatInt'

interface Organization {
  id: number
  name: string
  slug: string
  public_token?: string
  member_role: string
}

interface Props {
  organization: Organization
}

const CATEGORY_LABELS: Record<string, string> = {
  aria_chat: 'Aria-Chat',
  task_work: 'Aufgaben-Arbeit',
  human_events: 'Mensch-Ereignisse',
}

const CATEGORY_ORDER = ['aria_chat', 'task_work', 'human_events']

function formatUnits(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toLocaleString('de-DE', { maximumFractionDigits: 2 })} Mio`
  if (n >= 1_000) return `${(n / 1_000).toLocaleString('de-DE', { maximumFractionDigits: 1 })}k`
  return n.toLocaleString('de-DE')
}

function formatEUR(n: number): string {
  return `${n.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`
}

// Balken-Pattern analog KontingentCockpit.tsx / SPEC §4a: relative Breite zur
// GRÖSSTEN der Kategorien, NICHT gegen einen fixen Cap — reine Verhältnis-
// Anzeige, kein Warnsignal (kein Guthaben-Panik-Cap-Bezug).
function CategoryBar({
  categoryKey,
  units,
  eur,
  eventCount,
  maxUnits,
  unavailableReason,
}: {
  categoryKey: string
  units: number
  eur: number
  eventCount?: number
  maxUnits: number
  /** gesetzt = Kategorie nicht ermittelbar (cockpit-api: null) -> "nicht verfuegbar" statt 0 */
  unavailableReason?: string | null
}) {
  if (unavailableReason !== undefined) {
    return (
      <div style={{ marginBottom: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9em' }}>
          <span>{CATEGORY_LABELS[categoryKey] ?? categoryKey}</span>
          <span style={{ color: '#666' }} title={unavailableReason ?? undefined}>nicht verfügbar</span>
        </div>
      </div>
    )
  }
  const widthPct = maxUnits > 0 && units > 0 ? Math.max((units / maxUnits) * 100, 2) : 0
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9em', marginBottom: 4 }}>
        <span>{CATEGORY_LABELS[categoryKey] ?? categoryKey}</span>
        <span style={{ color: '#666' }}>
          {formatUnits(units)} Einheiten · {formatEUR(eur)}
          {eventCount !== undefined && eventCount > 0 && ` · ${eventCount} Ereignis${eventCount === 1 ? '' : 'se'}`}
        </span>
      </div>
      <div style={{ width: '100%', background: '#eee', height: 10, borderRadius: 5, overflow: 'hidden' }}>
        <div
          style={{ width: `${widthPct}%`, height: '100%', background: '#0c044d', transition: 'width 0.4s' }}
          aria-label={`${CATEGORY_LABELS[categoryKey] ?? categoryKey}: ${formatUnits(units)} Einheiten`}
        />
      </div>
    </div>
  )
}

export default function Usage({ organization }: Props) {
  const { token } = useAuth()
  const [data, setData] = useState<UsageResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    const personal = isPersonalMode()
    if (!token) return
    if (!personal && !organization?.slug) return
    api.setToken(token)
    setLoading(true)
    setError(null)
    try {
      const r = personal ? await api.getMyUsage() : await api.getOrgUsage(organization.slug)
      setData(r)
    } catch (err) {
      console.error('[Usage] load failed:', err)
      setError('Verbrauchsdaten konnten nicht geladen werden.')
    } finally {
      setLoading(false)
    }
  }, [token, organization?.slug])

  useEffect(() => {
    void load()
  }, [load])

  if (loading && !data) {
    return (
      <div style={{ padding: 16 }}>
        <h1>Verbrauch</h1>
        <p>Lade Verbrauchsdaten …</p>
      </div>
    )
  }

  if (error && !data) {
    return (
      <div style={{ padding: 16 }}>
        <h1>Verbrauch</h1>
        <p style={{ color: '#c00' }}>{error}</p>
        <button onClick={() => void load()}>Erneut versuchen</button>
      </div>
    )
  }

  if (!data) return null

  const categories = data.categories ?? {}
  // cockpit-api liefert total_* (null = nicht vollstaendig ermittelbar) und partial/unavailable.
  // Nie selbst aufsummieren, sonst erscheint bei fehlenden Kategorien eine zu niedrige Summe.
  // Geteiltes Cockpit (kein total_units-Feld): wie bisher summieren.
  const hasTotals = data.total_units !== undefined
  const totalUnits = hasTotals
    ? data.total_units ?? null
    : CATEGORY_ORDER.reduce((sum, k) => sum + (categories[k as keyof typeof categories]?.units ?? 0), 0)
  const totalEUR = hasTotals
    ? data.total_eur ?? null
    : CATEGORY_ORDER.reduce((sum, k) => sum + (categories[k as keyof typeof categories]?.eur ?? 0), 0)
  const unavailable = data.unavailable ?? CATEGORY_ORDER.filter((k) => categories[k as keyof typeof categories] === null)
  const tt = data.task_tokens
  const maxUnits = Math.max(...CATEGORY_ORDER.map((k) => categories[k as keyof typeof categories]?.units ?? 0), 0)

  return (
    <div style={{ padding: 16, maxWidth: 720 }}>
      <h1>Verbrauch diesen Monat</h1>
      <p style={{ color: '#666', fontSize: '0.9em' }}>{data.month}</p>

      <div style={{ background: '#f7f7f7', borderRadius: 8, padding: 16, marginBottom: 16 }}>
        <div style={{ fontSize: '1.1em', fontWeight: 600, marginBottom: 8 }}>
          {totalUnits === null || totalEUR === null
            ? 'Gesamtverbrauch nicht verfügbar'
            : `${formatUnits(totalUnits)} Einheiten verbraucht · ${formatEUR(totalEUR)}`}
        </div>
        {(data.partial || unavailable.length > 0) && (
          <p style={{ color: '#666', fontSize: '0.85em', margin: '0 0 12px' }}>
            Der Verbrauch ist nur teilweise ermittelbar{unavailable.length > 0 ? ` (nicht verfügbar: ${unavailable.map((k) => CATEGORY_LABELS[k] ?? k).join(', ')})` : ''}.
          </p>
        )}
        {CATEGORY_ORDER.map((key) => {
          // Mit task_tokens kommen Auftrags-Tokens separat (Karte unten); task_work waere hier immer 0.
          if (key === 'task_work' && tt) return null
          const cat = categories[key as keyof typeof categories]
          if (cat === null || unavailable.includes(key)) {
            return <CategoryBar key={key} categoryKey={key} units={0} eur={0} maxUnits={maxUnits} unavailableReason={data.unavailable_reasons?.[key] ?? null} />
          }
          if (!cat) return null
          // SPEC §7 Q1 (Patric-Gate 27.07.): Mensch-Ereignisse nur zeigen, wenn
          // der Tenant tatsächlich welche hat (aktuell nur SP-Pilot-Guthaben-
          // Modell) — sonst zeigt jeder Kunde eine leere/irrelevante dritte Zeile.
          if (key === 'human_events' && cat.units === 0 && !cat.event_count) return null
          return (
            <CategoryBar
              key={key}
              categoryKey={key}
              units={cat.units}
              eur={cat.eur}
              eventCount={cat.event_count}
              maxUnits={maxUnits}
            />
          )
        })}
      </div>

      {tt && (
        <div data-testid="task-tokens" style={{ background: '#f7f7f7', borderRadius: 8, padding: 16, marginBottom: 16 }}>
          <div style={{ fontSize: '1.05em', fontWeight: 600, marginBottom: 8 }}>KI-Aufträge (Tokens)</div>
          {tt.available === false ? (
            <p style={{ color: '#666', fontSize: '0.9em', margin: 0 }}>Derzeit nicht verfügbar.</p>
          ) : (
            <>
              <div style={{ fontSize: '0.9em' }}>
                <div>Erledigte Aufträge: {formatInt(tt.tasks_done)}</div>
                <div>Tokens gesamt: {formatInt(tt.tokens_total)}</div>
                <div style={{ color: '#666' }}>
                  Eingabe: {formatInt(tt.tokens_input)} · Ausgabe: {formatInt(tt.tokens_output)}
                </div>
              </div>
              {tt.only_done === true && (
                <p style={{ color: '#666', fontSize: '0.85em', margin: '8px 0 0' }}>Es zählen nur erledigte Aufträge.</p>
              )}
            </>
          )}
        </div>
      )}

      {error && <p style={{ color: '#c00', fontSize: '0.85em' }}>{error}</p>}
    </div>
  )
}
